import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  Mail, 
  Users, 
  Package, 
  Check, 
  Printer,
  Loader2,
  Search,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  Filter
} from "lucide-react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, color = "green" }) => (
  <div className="bg-white border border-lws-ink/10 p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 text-lws-${color}`} />
      <span className="text-xs uppercase tracking-wider text-lws-taupe">{label}</span>
    </div>
    <p className="text-3xl font-serif text-lws-ink">{value}</p>
  </div>
);

const LabelPreview = ({ label, onPrint }) => (
  <div className="bg-white border-2 border-dashed border-lws-ink/20 p-6 text-center">
    <pre className="font-mono text-sm text-lws-ink whitespace-pre-wrap text-left inline-block">
      {label.label_text}
    </pre>
    <div className="mt-4 pt-4 border-t border-lws-ink/10">
      <button
        onClick={() => onPrint(label)}
        className="inline-flex items-center gap-2 px-4 py-2 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
      >
        <Printer className="w-4 h-4" />
        Print Label
      </button>
    </div>
  </div>
);

export const AdminForwardingPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("queue");
  const [statusFilter, setStatusFilter] = useState("received");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [labelPreviews, setLabelPreviews] = useState([]);
  const [showLabelModal, setShowLabelModal] = useState(false);
  
  // New log form
  const [newLog, setNewLog] = useState({
    sender_member_id: "",
    recipient_member_id: "",
    is_international: false,
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, membersRes, logsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/forwarding/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/forwarding/members`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/forwarding/logs?status=${statusFilter}`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setMembers(membersRes.data.members);
      setLogs(logsRes.data.logs);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (status = statusFilter) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/forwarding/logs?status=${status}`,
        { withCredentials: true }
      );
      setLogs(res.data.logs);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/forwarding/logs`,
        newLog,
        { withCredentials: true }
      );
      
      alert(response.data.message);
      setNewLog({ sender_member_id: "", recipient_member_id: "", is_international: false, notes: "" });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to create log");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (logId, newStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/forwarding/logs/${logId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      fetchLogs();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleGetLabel = async (memberId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/forwarding/members/${memberId}/label`,
        { withCredentials: true }
      );
      setLabelPreviews([response.data]);
      setShowLabelModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to get label");
    }
  };

  const handleBatchLabels = async () => {
    if (selectedLabels.length === 0) {
      alert("Select at least one member for batch labels");
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/forwarding/labels/batch`,
        selectedLabels,
        { withCredentials: true }
      );
      setLabelPreviews(response.data.labels);
      setShowLabelModal(true);
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to get labels");
    }
  };

  const printLabel = (label) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print labels');
      return;
    }
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Shipping Label - ${DOMPurify.sanitize(label.member_id)}</title>
          <style>
            body { 
              font-family: 'Courier New', monospace; 
              padding: 20px; 
              font-size: 14px;
            }
            .label {
              border: 2px solid #000;
              padding: 20px;
              max-width: 4in;
              margin: 0 auto;
            }
            @media print {
              body { padding: 0; }
              .label { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="label">
            <pre>${DOMPurify.sanitize(label.label_text)}</pre>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const filteredMembers = members.filter(m => 
    m.member_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-lws-rose mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-lws-ink mb-2">Admin Access Required</h1>
          <p className="text-lws-taupe">Please log in with an admin account.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lws-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lws-cream" data-testid="admin-forwarding-page">
      {/* Header */}
      <div className="bg-lws-green text-lws-paper py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-serif">Forwarding Admin Portal</h1>
          <p className="text-lws-paper/80 mt-2">Manage letter forwarding, members, and print labels</p>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-6xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Members" value={stats?.total_members || 0} />
          <StatCard icon={Check} label="Active Subscribers" value={stats?.active_subscribers || 0} color="green" />
          <StatCard icon={Package} label="Pending Letters" value={stats?.pending_letters || 0} color="amber" />
          <StatCard icon={Mail} label="Total Forwarded" value={stats?.forwarded_letters || 0} />
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 mt-8">
        <div className="flex border-b border-lws-ink/10">
          {["queue", "members", "log-letter"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm uppercase tracking-wider transition-colors ${
                activeTab === tab 
                  ? 'border-b-2 border-lws-green text-lws-green' 
                  : 'text-lws-taupe hover:text-lws-ink'
              }`}
            >
              {tab === "queue" ? "Forwarding Queue" : tab === "members" ? "Members & Labels" : "Log New Letter"}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Forwarding Queue Tab */}
        {activeTab === "queue" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Filter className="w-5 h-5 text-lws-taupe" />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); fetchLogs(e.target.value); }}
                  className="border border-lws-ink/20 rounded px-3 py-2 text-sm"
                >
                  <option value="received">Received (Pending)</option>
                  <option value="forwarded">Forwarded</option>
                  <option value="returned">Returned</option>
                  <option value="held">Held</option>
                </select>
              </div>
              <button
                onClick={() => fetchLogs()}
                className="flex items-center gap-2 text-sm text-lws-taupe hover:text-lws-green"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {logs.length === 0 ? (
              <div className="text-center py-12 text-lws-taupe">
                No letters with status &quot;{statusFilter}&quot;
              </div>
            ) : (
              <div className="space-y-4">
                {logs.map((log) => (
                  <div key={log.log_id} className="bg-white border border-lws-ink/10 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm text-lws-green">{log.log_id}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            log.is_international ? 'bg-lws-amber/10 text-lws-amber' : 'bg-lws-ink/5 text-lws-taupe'
                          }`}>
                            {log.is_international ? 'International' : 'Domestic'}
                          </span>
                        </div>
                        <p className="text-lws-ink mt-1">
                          <strong>{log.sender_member_id}</strong> → <strong>{log.recipient_member_id}</strong>
                        </p>
                        <p className="text-xs text-lws-taupe mt-1">
                          Received: {new Date(log.date_received).toLocaleDateString()}
                          {log.notes && ` • ${log.notes}`}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {log.status === "received" && (
                          <>
                            <button
                              onClick={() => handleGetLabel(log.recipient_member_id)}
                              className="px-3 py-1.5 border border-lws-ink/20 text-xs uppercase tracking-wider hover:border-lws-green hover:text-lws-green transition-colors"
                            >
                              <Printer className="w-3 h-3 inline mr-1" />
                              Label
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(log.log_id, "forwarded")}
                              className="px-3 py-1.5 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
                            >
                              Mark Forwarded
                            </button>
                          </>
                        )}
                        {log.status === "forwarded" && (
                          <span className="px-3 py-1.5 bg-lws-green/10 text-lws-green text-xs uppercase tracking-wider">
                            <Check className="w-3 h-3 inline mr-1" />
                            Forwarded
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Members & Labels Tab */}
        {activeTab === "members" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-lws-taupe" />
                <input
                  type="text"
                  placeholder="Search by ID, name, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-lws-ink/20 rounded"
                />
              </div>
              
              {selectedLabels.length > 0 && (
                <button
                  onClick={handleBatchLabels}
                  className="flex items-center gap-2 px-4 py-2 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print {selectedLabels.length} Labels
                </button>
              )}
            </div>

            <div className="bg-white border border-lws-ink/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-lws-ink/5">
                  <tr>
                    <th className="w-10 p-3">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLabels(filteredMembers.filter(m => m.forwarding_active).map(m => m.member_id));
                          } else {
                            setSelectedLabels([]);
                          }
                        }}
                        checked={selectedLabels.length === filteredMembers.filter(m => m.forwarding_active).length && selectedLabels.length > 0}
                      />
                    </th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-lws-taupe">Member ID</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-lws-taupe">Name</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-lws-taupe">Country</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-lws-taupe">Status</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-lws-taupe">Credits</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-lws-taupe">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member) => (
                    <tr key={member.member_id} className="border-t border-lws-ink/5 hover:bg-lws-ink/5">
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedLabels.includes(member.member_id)}
                          disabled={!member.forwarding_active}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLabels([...selectedLabels, member.member_id]);
                            } else {
                              setSelectedLabels(selectedLabels.filter(id => id !== member.member_id));
                            }
                          }}
                        />
                      </td>
                      <td className="p-3 font-mono text-sm text-lws-green">{member.member_id}</td>
                      <td className="p-3">
                        <div className="text-lws-ink">{member.name || member.address?.full_name || '-'}</div>
                        <div className="text-xs text-lws-taupe">{member.email}</div>
                      </td>
                      <td className="p-3 text-lws-taupe">{member.country || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          member.forwarding_active 
                            ? 'bg-lws-green/10 text-lws-green' 
                            : 'bg-lws-ink/5 text-lws-taupe'
                        }`}>
                          {member.forwarding_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3 text-lws-ink">{member.letter_credits || 0}</td>
                      <td className="p-3 text-right">
                        {member.forwarding_active && member.address && (
                          <button
                            onClick={() => handleGetLabel(member.member_id)}
                            className="text-xs text-lws-green hover:text-lws-navy"
                          >
                            <Printer className="w-4 h-4 inline mr-1" />
                            Print Label
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Log New Letter Tab */}
        {activeTab === "log-letter" && (
          <div className="max-w-xl">
            <div className="bg-white border border-lws-ink/10 p-8">
              <h2 className="text-xl font-serif text-lws-ink mb-6">Log Received Letter</h2>
              
              <form onSubmit={handleCreateLog} className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-lws-taupe mb-2">
                    Sender Member ID
                  </label>
                  <input
                    type="text"
                    value={newLog.sender_member_id}
                    onChange={(e) => setNewLog({...newLog, sender_member_id: e.target.value.toUpperCase()})}
                    placeholder="LWS-0001"
                    className="w-full border border-lws-ink/20 rounded px-4 py-3 font-mono"
                    required
                    data-testid="sender-id-input"
                  />
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider text-lws-taupe mb-2">
                    Recipient Member ID
                  </label>
                  <input
                    type="text"
                    value={newLog.recipient_member_id}
                    onChange={(e) => setNewLog({...newLog, recipient_member_id: e.target.value.toUpperCase()})}
                    placeholder="LWS-0002"
                    className="w-full border border-lws-ink/20 rounded px-4 py-3 font-mono"
                    required
                    data-testid="recipient-id-input"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_international"
                    checked={newLog.is_international}
                    onChange={(e) => setNewLog({...newLog, is_international: e.target.checked})}
                    className="w-5 h-5 text-lws-green"
                  />
                  <label htmlFor="is_international" className="text-lws-ink">
                    International letter
                  </label>
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider text-lws-taupe mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    value={newLog.notes}
                    onChange={(e) => setNewLog({...newLog, notes: e.target.value})}
                    placeholder="Any special notes..."
                    className="w-full border border-lws-ink/20 rounded px-4 py-3 h-24"
                  />
                </div>
                
                <div className="bg-lws-amber/10 border border-lws-amber/20 p-4 rounded text-sm text-lws-ink">
                  <strong>Note:</strong> Logging a letter will automatically deduct 1 credit from the sender&apos;s account.
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors disabled:opacity-50"
                  data-testid="log-letter-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Log Letter & Deduct Credit"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Label Preview Modal */}
      {showLabelModal && (
        <div className="fixed inset-0 bg-lws-ink/50 flex items-center justify-center z-50 p-6">
          <div className="bg-lws-paper rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-lws-ink">
                Label Preview ({labelPreviews.length})
              </h2>
              <button
                onClick={() => setShowLabelModal(false)}
                className="text-lws-taupe hover:text-lws-ink"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {labelPreviews.map((label) => (
                <LabelPreview key={label.member_id} label={label} onPrint={printLabel} />
              ))}
            </div>
            
            {labelPreviews.length > 1 && (
              <div className="mt-6 pt-6 border-t border-lws-ink/10 text-center">
                <button
                  onClick={() => labelPreviews.forEach(printLabel)}
                  className="px-6 py-3 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
                >
                  <Printer className="w-4 h-4 inline mr-2" />
                  Print All Labels
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminForwardingPage;
